if (!customElements.get('bearific-carousel')) {
  class BearificCarousel extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('[data-track]');
      this.cards = Array.from(this.querySelectorAll('.bearific-carousel__card'));
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      this.pagination = this.querySelector('[data-pagination]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.fractionLabel = this.querySelector('[data-fraction]');
      
      this.currentIndex = 0;
      this.cardsPerSlideDesktop = parseInt(this.dataset.cardsPerSlide) || 3;
      this.paginationStyle = this.dataset.paginationStyle || 'numbers';
      this.autoRotate = this.dataset.autoRotate === 'true';
      this.autoRotateSpeed = parseInt(this.dataset.autoRotateSpeed) || 5;
      this.autoRotateTimer = null;
      
      // Touch/Swipe properties
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.touchStartY = 0;
      this.touchEndY = 0;
      this.isDragging = false;
      this.startTranslate = 0;
      this.currentTranslate = 0;
      this.minSwipeDistance = 50; // Minimum distance for a swipe to register
      
      this.init();
    }

    init() {
      // Set data attribute on cards for responsive width
      this.cards.forEach(card => {
        card.setAttribute('data-cards', this.cardsPerSlideDesktop);
      });

      // Setup event listeners
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.prev());
      }
      
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.next());
      }

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index));
      });

      // Touch/Swipe event listeners
      this.track.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
      this.track.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
      this.track.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });

      // Auto rotate
      if (this.autoRotate) {
        this.startAutoRotate();
        
        // Pause on hover and touch
        this.addEventListener('mouseenter', () => this.stopAutoRotate());
        this.addEventListener('mouseleave', () => this.startAutoRotate());
        this.addEventListener('touchstart', () => this.stopAutoRotate());
        this.addEventListener('touchend', () => this.startAutoRotate());
      }

      // Update on window resize
      window.addEventListener('resize', () => this.updateCarousel());

      // Initial update
      this.updateCarousel();
    }

    getCardsPerSlide() {
      // On mobile (< 750px), show 1 card at a time
      if (window.innerWidth < 750) {
        return 1;
      }
      return this.cardsPerSlideDesktop;
    }

    getMaxIndex() {
      return Math.max(0, this.cards.length - this.getCardsPerSlide());
    }

    prev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.updateCarousel();
      }
    }

    next() {
      const maxIndex = this.getMaxIndex();
      if (this.currentIndex < maxIndex) {
        this.currentIndex++;
        this.updateCarousel();
      }
    }

    goToSlide(index) {
      const maxIndex = this.getMaxIndex();
      this.currentIndex = Math.min(index, maxIndex);
      this.updateCarousel();
    }

    updateCarousel() {
      const maxIndex = this.getMaxIndex();
      const cardsPerSlide = this.getCardsPerSlide();
      const paginationStyle = this.paginationStyle;
      
      // Ensure current index is within bounds
      if (this.currentIndex > maxIndex) {
        this.currentIndex = maxIndex;
      }
      
      // Calculate slide position
      const cardWidth = this.cards[0]?.offsetWidth || 0;
      const gap = parseInt(getComputedStyle(this.track).gap) || 0;
      const offset = -(this.currentIndex * (cardWidth + gap));
      
      this.track.style.transform = `translateX(${offset}px)`;

      // Update buttons state
      if (this.prevButton) {
        this.prevButton.disabled = this.currentIndex === 0;
      }
      
      if (this.nextButton) {
        this.nextButton.disabled = this.currentIndex === maxIndex;
      }

      // Update pagination based on style
      if (paginationStyle === 'fraction' && this.fractionLabel) {
        // Fraction mode: update single label
        if (cardsPerSlide === 1) {
          // Mobile: show current card out of total cards
          this.fractionLabel.textContent = `${this.currentIndex + 1}/${this.cards.length}`;
        } else {
          // Desktop: show current slide out of total slides
          this.fractionLabel.textContent = `${this.currentIndex + 1}/${maxIndex + 1}`;
        }
      } else {
        // Numbers mode: update dots visibility and active state
        this.dots.forEach((dot, index) => {
          // On desktop, only show dots for valid slide positions
          // On mobile, show all dots (one per card)
          if (cardsPerSlide === 1) {
            // Mobile: show all dots
            dot.style.display = '';
            if (index === this.currentIndex) {
              dot.classList.add('is-active');
            } else {
              dot.classList.remove('is-active');
            }
          } else {
            // Desktop: only show dots for valid slide starts
            if (index <= maxIndex) {
              dot.style.display = '';
              if (index === this.currentIndex) {
                dot.classList.add('is-active');
              } else {
                dot.classList.remove('is-active');
              }
            } else {
              dot.style.display = 'none';
              dot.classList.remove('is-active');
            }
          }
        });
      }
    }

    startAutoRotate() {
      if (!this.autoRotate) return;
      
      this.stopAutoRotate();
      this.autoRotateTimer = setInterval(() => {
        const maxIndex = this.getMaxIndex();
        if (this.currentIndex >= maxIndex) {
          this.currentIndex = 0;
        } else {
          this.currentIndex++;
        }
        this.updateCarousel();
      }, this.autoRotateSpeed * 1000);
    }

    stopAutoRotate() {
      if (this.autoRotateTimer) {
        clearInterval(this.autoRotateTimer);
        this.autoRotateTimer = null;
      }
    }

    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.isDragging = true;
      
      // Store the current transform position
      const cardWidth = this.cards[0]?.offsetWidth || 0;
      const gap = parseInt(getComputedStyle(this.track).gap) || 0;
      this.startTranslate = -(this.currentIndex * (cardWidth + gap));
    }

    handleTouchMove(e) {
      if (!this.isDragging) return;
      
      this.touchEndX = e.touches[0].clientX;
      this.touchEndY = e.touches[0].clientY;
      
      const deltaX = this.touchEndX - this.touchStartX;
      const deltaY = this.touchEndY - this.touchStartY;
      
      // If user is scrolling vertically more than horizontally, don't prevent default
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        return;
      }
      
      // Prevent default to stop page scroll when swiping horizontally
      e.preventDefault();
      
      // Apply transform for visual feedback during swipe
      this.currentTranslate = this.startTranslate + deltaX;
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(${this.currentTranslate}px)`;
    }

    handleTouchEnd(e) {
      if (!this.isDragging) return;
      
      this.isDragging = false;
      
      const deltaX = this.touchEndX - this.touchStartX;
      const deltaY = this.touchEndY - this.touchStartY;
      
      // Check if this was a horizontal swipe
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > this.minSwipeDistance) {
        if (deltaX > 0) {
          // Swiped right - go to previous
          this.prev();
        } else {
          // Swiped left - go to next
          this.next();
        }
      } else {
        // Not a valid swipe, snap back to current position
        this.updateCarousel();
      }
      
      // Re-enable transition
      this.track.style.transition = '';
      
      // Reset touch coordinates
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.touchStartY = 0;
      this.touchEndY = 0;
    }

    disconnectedCallback() {
      this.stopAutoRotate();
    }
  }

  customElements.define('bearific-carousel', BearificCarousel);
}
